import React, { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Image as ImageIcon, Loader2 } from 'lucide-react';
import {
  isSpriteSheetPrefetched,
  markSpriteSheetLoaded,
  useSpriteSheetPrefetch
} from '../../hooks/useSpriteSheetPrefetch';

export interface SpriteSheetConfig {
  columns: number;
  rows: number;
  frameCount: number;
  frameWidth?: number;
  frameHeight?: number;
  sheets?: Array<{
    url: string;
    startFrame: number;
    endFrame: number;
  }>;
}

interface SpriteSheetPlayerProps {
  thumbnailUrl: string;
  fallbackVideoUrl?: string;
  spriteSheetUrl?: string;
  spriteSheetConfig?: SpriteSheetConfig;
  aspectRatio?: string;
  className?: string;
  children?: React.ReactNode;
  onClick?: () => void;
  prefetch?: boolean;
  alt?: string;
}

const DEFAULT_CONFIG: SpriteSheetConfig = {
  columns: 10,
  rows: 10,
  frameCount: 100
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const parseAspectRatio = (ratio: string) => {
  const [width, height] = ratio.split('/').map(Number);
  return width && height ? width / height : 16 / 9;
};

const SpriteSheetPlayer: React.FC<SpriteSheetPlayerProps> = ({
  thumbnailUrl,
  fallbackVideoUrl,
  spriteSheetUrl,
  spriteSheetConfig,
  aspectRatio = '16/9',
  className = '',
  children,
  onClick,
  prefetch = false,
  alt = ''
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const config = useMemo(() => ({
    ...DEFAULT_CONFIG,
    ...(spriteSheetConfig || {})
  }), [spriteSheetConfig]);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoaded, setIsLoaded] = useState(() => isSpriteSheetPrefetched(spriteSheetUrl));
  const [isLoading, setIsLoading] = useState(false);
  const [hasThumbnailError, setHasThumbnailError] = useState(false);
  const [hasSpriteError, setHasSpriteError] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const [frameIndex, setFrameIndex] = useState(0);
  const [scrubProgress, setScrubProgress] = useState(0);

  useSpriteSheetPrefetch(cardRef, [spriteSheetUrl], prefetch);

  useEffect(() => {
    setHasThumbnailError(false);
  }, [thumbnailUrl]);

  useEffect(() => {
    setIsLoaded(isSpriteSheetPrefetched(spriteSheetUrl));
    setIsLoading(false);
    setHasSpriteError(false);
    setFrameIndex(0);
    setScrubProgress(0);
  }, [spriteSheetUrl]);

  useEffect(() => {
    setHasVideoError(false);
  }, [fallbackVideoUrl]);

  const loadSpriteSheet = () => {
    if (!spriteSheetUrl || isLoaded || isLoading || hasSpriteError) {
      return;
    }

    setIsLoading(true);
    const image = new Image();
    image.onload = () => {
      markSpriteSheetLoaded(spriteSheetUrl);
      setIsLoaded(true);
      setIsLoading(false);
    };
    image.onerror = () => {
      setHasSpriteError(true);
      setIsLoading(false);
    };
    image.src = spriteSheetUrl;
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    loadSpriteSheet();
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setFrameIndex(0);
    setScrubProgress(0);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current || !isLoaded || hasSpriteError) {
      return;
    }

    const rect = cardRef.current.getBoundingClientRect();
    const x = clamp(event.clientX - rect.left, 0, rect.width);
    const progress = rect.width ? x / rect.width : 0;
    const nextFrame = Math.min(
      Math.floor(progress * config.frameCount),
      config.frameCount - 1
    );

    setScrubProgress(progress);
    setFrameIndex(nextFrame);
  };

  const col = frameIndex % config.columns;
  const row = Math.floor(frameIndex / config.columns);
  const positionX = config.columns > 1 ? `${(col / (config.columns - 1)) * 100}%` : '0%';
  const positionY = config.rows > 1 ? `${(row / (config.rows - 1)) * 100}%` : '0%';
  const canPreview = isHovered && isLoaded && spriteSheetUrl && !hasSpriteError;
  const canUseVideoFallback = !!fallbackVideoUrl && (!spriteSheetUrl || hasSpriteError);
  const canPreviewVideo = isHovered && canUseVideoFallback && !hasVideoError;
  const frameRatio = config.frameWidth && config.frameHeight ? config.frameWidth / config.frameHeight : parseAspectRatio(aspectRatio);
  const cardRatio = parseAspectRatio(aspectRatio);
  const spriteFrameStyle = frameRatio >= cardRatio
    ? { aspectRatio: frameRatio, width: '100%' }
    : { aspectRatio: frameRatio, height: '100%' };

  return (
    <div
      ref={cardRef}
      className={`group/media relative overflow-hidden bg-black ${onClick ? 'cursor-pointer' : ''} ${className}`}
      style={{ aspectRatio }}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      {canPreview ? (
        <div className="flex h-full w-full items-center justify-center bg-black">
          <div
            style={{
              ...spriteFrameStyle,
              backgroundImage: `url(${spriteSheetUrl})`,
              backgroundSize: `${config.columns * 100}% ${config.rows * 100}%`,
              backgroundPosition: `${positionX} ${positionY}`,
              backgroundRepeat: 'no-repeat'
            }}
          />
        </div>
      ) : thumbnailUrl && !hasThumbnailError ? (
        <img
          src={thumbnailUrl}
          alt={alt}
          className="h-full w-full object-cover transition-transform duration-500 ease-out"
          onError={() => setHasThumbnailError(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gray-100 text-gray-300">
          <ImageIcon className="h-8 w-8" />
        </div>
      )}

      {canPreviewVideo && (
        <video
          ref={videoRef}
          src={fallbackVideoUrl}
          className="absolute inset-0 z-10 h-full w-full bg-black object-contain"
          muted
          loop
          playsInline
          autoPlay
          preload="metadata"
          onCanPlay={() => {
            void videoRef.current?.play().catch(() => undefined);
          }}
          onError={() => setHasVideoError(true)}
        />
      )}

      {isHovered && isLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/20 text-white">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {children}

      {isHovered && spriteSheetUrl && !hasSpriteError && (
        <div className="absolute inset-x-0 bottom-0 z-30 h-1 bg-white/30">
          <div
            className="h-full bg-amber-300 transition-[width] duration-75"
            style={{ width: `${scrubProgress * 100}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default SpriteSheetPlayer;
